import {
    Quest,
    QuestAnswerOption,
    QuestDisplayType,
    QuestionAppearance,
    QuestionType,
    QuestPart,
    QuestQuestion,
    QuestQuestionGroup,
    QuestQuestionValidate,
    QuestTransform,
    QuestValidators,
} from '@vi/quest';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { getProperty, intervalValues, isVisible, singletonValues, toIsoDate } from '../../../utils/configit-ace.helper';
import {
    ConfigitAceAssignment,
    ConfigitAceConfiguration,
    ConfigitAceItem,
    ConfigitAceSection,
    ConfigitAceValueType,
    ConfigitAceVariable,
} from '../../../models/configit-ace.model';
import { environment } from '../../../../../environments/environment';

export class ConfigitAceQuestModelTransform implements QuestTransform {
    constructor(private translate: TranslateService) {}

    public transform(model: ConfigitAceConfiguration): Quest {
        const id = model.product.id;
        const display = QuestDisplayType.stepped;
        const original = model;
        const parts = model.sections.map((section) => this.transformPart(section, model));
        return { id, display, original, parts };
    }

    private transformPart(section: ConfigitAceSection, configuration: ConfigitAceConfiguration): QuestPart {
        const questions: QuestQuestion[] = section.variables
            .filter(isVisible)
            .map((variable) => this.transformQuestion(variable, configuration));
        const groups = section.sections.map((s) => this.transformGroup(s, configuration));
        return {
            id: section.id,
            title: section.name,
            icon: null,
            headline: section.name,
            description: getProperty('INFOTEXT', section),
            valid: !this.hasError(questions),
            questions,
            groups,
            disabled: false,
        };
    }

    private transformGroup(section: ConfigitAceSection, configuration: ConfigitAceConfiguration): QuestQuestionGroup {
        const questions: QuestQuestion[] = section.variables
            .filter(isVisible)
            .map((variable) => this.transformQuestion(variable, configuration));
        const subGroups = section.sections.map((subSection) => this.transformGroup(subSection, configuration));

        return {
            id: section.id,
            headline: section.name,
            description: '',
            questions,
            subGroups,
            error: this.hasError(questions),
        };
    }

    private transformQuestion(
        variable: ConfigitAceVariable,
        { configuration }: ConfigitAceConfiguration
    ): QuestQuestion {
        const assignments = configuration.configurationAssignments.filter((a) => a.variableId === variable.id);
        const type = this.transformType(variable);
        const value = this.transformValue(type, variable, assignments);
        const options = this.transformOptions(variable);
        const hint = this.transformHint(variable);
        const appearance = this.transformAppearance(variable);
        const validate = this.transformValidation(type, variable);
        const placeholder = this.transformPlaceholder(type, validate);
        const error = this.transformError(validate, value);

        return {
            id: variable.id,
            type,
            text: variable.name,
            hint,
            placeholder,
            value,
            options,
            error,
            validate,
            appearance,
            disabled: variable.readOnly,
        };
    }

    private transformType(variable: ConfigitAceVariable): QuestionType {
        if (variable.allowMultipleAssignments) {
            return QuestionType.checklist;
        }
        return this.getTypeByDisplayAs(variable) || QuestionType.text;
    }

    private getTypeByDisplayAs(variable: ConfigitAceVariable) {
        const displayAs = getProperty('DISPLAYAS', variable);
        if (displayAs) {
            switch (displayAs) {
                case 'Card':
                    return QuestionType.card;
                case 'RadioButton':
                    return variable.valueType === ConfigitAceValueType.boolean
                        ? QuestionType.checkbox
                        : QuestionType.radio;
                case 'DropDown':
                    return QuestionType.dropdown;
                case 'Hint':
                    return QuestionType.hint;
                case 'InputField':
                    return this.getInputFieldByValueType(variable);
                case 'Date':
                    return QuestionType.date;
            }
        }
        return undefined;
    }

    private getInputFieldByValueType(variable: ConfigitAceVariable) {
        if (variable.valueType === ConfigitAceValueType.number) {
            return variable.scale > 0 ? QuestionType.float : QuestionType.integer;
        }
        return QuestionType.text;
    }

    private transformValue(type: QuestionType, variable: ConfigitAceVariable, assignments: ConfigitAceAssignment[]) {
        const values = singletonValues(variable.values);
        if (type === QuestionType.checklist) {
            return assignments.map((a) => a.value);
        }
        // should we take value from assignments in any case?
        const value = values.find((v) => !!v.assigned)?.value;
        if (type === QuestionType.date) {
            // quest lib needs date in iso format
            return toIsoDate(value);
        }
        return value;
    }

    private transformOptions(variable: ConfigitAceVariable): QuestAnswerOption[] {
        return singletonValues(variable.values).map((value) => {
            return {
                id: value.value,
                description: getProperty('INFOTEXT', value),
                image: this.getImageUrl(value),
                value: value.value,
                text: value.name,
                disabled: false,
                custom: value.incompatible ? 'conflicting' : undefined,
            };
        });
    }

    private transformHint(variable: ConfigitAceVariable): { text: string; important: boolean } {
        const text = getProperty('INFOTEXT', variable);
        return text ? { text, important: false } : undefined;
    }

    private transformAppearance(variable: ConfigitAceVariable): QuestionAppearance {
        const appearanceWidth = <'Half' | 'Full' | 'HalfStandalone'>getProperty('APPEARANCEWIDTH', variable);
        switch (appearanceWidth) {
            case 'HalfStandalone': {
                return QuestionAppearance.halfStandalone;
            }
            case 'Full': {
                return QuestionAppearance.full;
            }
            default: {
                return QuestionAppearance.half;
            }
        }
    }

    private transformValidation(type: QuestionType, variable: ConfigitAceVariable): QuestQuestionValidate {
        return {
            email: getProperty('DISPLAYAS', variable) === 'Email',
            date: type === QuestionType.date,
            integer: type === QuestionType.integer,
            float: type === QuestionType.float,
            ...this.getMinMax(type, variable),
            required: getProperty('REQUIRED', variable),
            decimalPlaces: type === QuestionType.float && variable.scale,
        };
    }

    private getMinMax(type: QuestionType, variable: ConfigitAceVariable) {
        if ([QuestionType.integer, QuestionType.float].includes(type)) {
            const intervals = intervalValues(variable.values);
            const values = variable.values
                .flatMap((value) => (value.type === 'SingletonValue' ? [value.value] : [value.lower, value.upper]))
                .filter((v) => v !== undefined); // NOT filter(Boolean) because we need "0" value
            const min = intervals.some((i) => i.isLowerInfinity) ? undefined : Math.min(...values);
            const max = intervals.some((i) => i.isUpperInfinity) ? undefined : Math.max(...values);
            return { min, max };
        }
        if (type === QuestionType.date) {
            const dateMin = getProperty('DATEMIN', variable);
            const dateMax = getProperty('DATEMAX', variable);
            if (dateMin || dateMax) {
                return { dateMin, dateMax };
            }
        }
        return {};
    }

    private transformPlaceholder(type: QuestionType, validate: QuestQuestionValidate): string | undefined {
        const optional = (!validate.required && ' ' + this.translated('placeholder.optional')) || '';

        if (
            [
                QuestionType.date,
                QuestionType.float,
                QuestionType.integer,
                QuestionType.dropdown,
                QuestionType.text,
            ].includes(type)
        ) {
            return this.translated(`placeholder.${QuestionType.text && validate.email ? 'email' : type}`) + optional;
        }
        return undefined;
    }

    private transformError(validate: QuestQuestionValidate, value: string | string[]) {
        if (value) {
            // helper methods
            const active: (rule: string) => any = (rule: string) => validate[rule] || validate[rule] === 0;
            const validator = (rule: string) => QuestValidators[rule](validate[rule]);
            // get validator functions for active rules
            const checks: ValidatorFn[] = Object.keys(validate).filter(active).map(validator);
            // then check those
            const control = new UntypedFormControl(value, checks);

            return !control.valid;
        }
        return undefined;
    }

    private hasError(questions: QuestQuestion[]): boolean {
        const errorprone = questions.filter(
            (question) => question.value || (question.validate && question.validate.required)
        );
        return errorprone.some((question) => question.error !== false) || false;
    }

    private getImageUrl(value: ConfigitAceItem) {
        const image = getProperty('IMAGE', value);
        if (image) {
            return `${environment.http.staticResources.replace(/\/$/, '')}/${image.trim()}`;
        }
        return undefined;
    }

    protected translated(text: string): string {
        return this.translate.instant(`quest.${text}`);
    }
}
